import { Page } from '@playwright/test';

export const onFormSubmitCallback = async (
	page: Page,
	formSelector: string
): Promise<() => Promise<Record<string, string>>> => {
	let formData: Record<string, string> | null = null;
	// Expose a callback function into the browser's context
	// that can call function in our context, too.
	// Used to receive the form data from the submission callback.
	await page.exposeFunction('callback', async (formDataAsJson: string) => {
		formData = JSON.parse(formDataAsJson);
	});

	await page.waitForTimeout(500);

	// Setup a submit listener on the form found by the formSelector.
	// Note that this runs in a separate browser context, so we cannot pass in
	// non-serializable variables, such as a function reference.
	const result = await page.evaluate((selector) => {
		const form = document.getElementById(selector);
		if (!(form instanceof HTMLFormElement)) {
			return new Error('Form to attach listeners to not found');
		}
		form.addEventListener('submit', async (event: SubmitEvent) => {
			event.preventDefault();
			const formData = new FormData(form);
			if ('callback' in window && typeof window.callback === 'function') {
				const data = Array.from(formData.keys()).reduce(
					(acc, current) => {
						if (formData.getAll(current).length > 1) {
							return { ...acc, [current]: formData.getAll(current) };
						}
						return { ...acc, [current]: formData.get(current)! };
					},
					{} as Record<string, FormDataEntryValue | FormDataEntryValue[]>
				);
				await window.callback(JSON.stringify(data));
			}
		});
		return true;
	}, formSelector);

	if (result instanceof Error) {
		throw result;
	}

	// Playwright handles the timeout and will break the loop when the test suite times out
	return async () => {
		while (!formData) {
			await page.waitForTimeout(100);
		}
		return formData;
	};
};
